import { appURLs } from "@/app/constants.mjs";
import type { PlatformType } from "@/game-fortnite/constants/platforms.mjs";
import {
  FORTNITE_PLATFORMS,
  FORTNITE_PLATFORMS_SYMBOLS,
} from "@/game-fortnite/constants/platforms.mjs";
import * as GraphQLTemplates from "@/game-fortnite/graphql-templates.mjs";
import type { Player } from "@/game-fortnite/models/profile.mjs";
import { postGQL, queryGQL } from "@/util/gql-formatters.mjs";

export function getPlayer(variables: { accountId?: string; name?: string }) {
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getProfile, variables);
}

export function getCurrentSeason(variables) {
  return queryGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.getCurrentSeason,
    variables,
  );
}

export function getSeasons() {
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getSeasons, {});
}

export function createPlayer(variables: { player: Player }) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.createPlayer, variables);
}

export function createMatch(variables) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.createMatch, variables);
}

export function getMatch(variables: {
  gameId?: string;
  id?: string;
  seasonId?: number;
}) {
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getMatch, variables);
}

export function getProfileMatchlist(variables: {
  accountId: string;
  name?: string;
  seasonId?: string;
  isRanked?: boolean;
  isZeroBuild?: boolean;
  mode?: string;
  offset?: number;
  first?: number;
}) {
  return queryGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.getProfileMatchlist,
    variables,
  );
}

export function updatePlayer(variables: { player: Player }) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.updatePlayer, variables);
}

export function getPlayerSummary(variables: {
  accountId: string;
  seasonId: string;
  isRanked: boolean;
  isZeroBuild: boolean;
  mode: string | null;
}) {
  return queryGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.getPlayerSummaries,
    variables,
  );
}

export function getPlayerWeaponStats(variables: {
  accountId: string;
  seasonId: string;
  isRanked: boolean;
  isZeroBuild: boolean;
  mode: string | null;
}) {
  return queryGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.getPlayerWeaponStats,
    variables,
  );
}

export function getPlayerRanks(variables) {
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getPlayerRanks, variables);
}

export function getWeaponStatistics(variables) {
  return queryGQL(
    appURLs.DATALAKE,
    GraphQLTemplates.weaponStatistics,
    variables,
  );
}

export function getRankStatistics(variables) {
  return queryGQL(appURLs.DATALAKE, GraphQLTemplates.rankStatistics, variables);
}

export function getWeaponHistoricStats(variables) {
  return queryGQL(
    appURLs.DATALAKE,
    GraphQLTemplates.weaponHistoricStats,
    variables,
  );
}

export function getItemShop() {
  return `${appURLs.UTILS_STATIC}/fortnite/item-shop-v2`;
}

export function getReactions(variables: {
  ids: string[];
  reactionType?: string;
  userId?: string;
}) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.getReactions, variables);
}

export function addReaction(variables: {
  userId: string;
  id: string;
  reactionType: string;
}) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.addReaction, variables);
}

export function removeReaction(variables: {
  userId: string;
  id: string;
  reactionType: string;
}) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.removeReaction, variables);
}

export function getLocker(variables: { fortniteAccountId: string }) {
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getLockerItems, variables);
}

export function addToLocker(variables: {
  shopItemId: string;
  fortniteAccountId: string;
}) {
  return postGQL(appURLs.FORTNITE, GraphQLTemplates.addToLocker, variables);
}

export function updateItemInLocker(variables: {
  shopItemId: string;
  fortniteAccountId: string;
}) {
  return postGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.updateItemInLocker,
    variables,
  );
}

export function getItems() {
  return `${appURLs.UTILS_STATIC}/fortnite/assets`;
}

export function getWeaponItems() {
  return `${appURLs.UTILS_STATIC}/fortnite/weapons`;
}

export function getActiveWeaponItems() {
  return `${appURLs.UTILS_STATIC}/fortnite/active`;
}

export function getVaultedWeaponItems() {
  return `${appURLs.UTILS_STATIC}/fortnite/vaulted`;
}

export function invokeConsoleSearch(variables: { identifier: string }) {
  return queryGQL(
    appURLs.FORTNITE,
    GraphQLTemplates.searchConsolePlayer,
    variables,
  );
}

export function getConsoleMatches(
  variables: {
    accountId: string;
    seasonId: string;
  },
  platform: PlatformType,
) {
  const activePlatform =
    Object.getOwnPropertySymbols(FORTNITE_PLATFORMS).find(
      (p) => FORTNITE_PLATFORMS[p].key === platform,
    ) || FORTNITE_PLATFORMS_SYMBOLS.console;
  return queryGQL(appURLs.FORTNITE, GraphQLTemplates.getConsoleMatches, {
    ...variables,
    inputType: FORTNITE_PLATFORMS[activePlatform].inputType,
  });
}
